import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownMessageProps {
  content: string;
  /** 'sm' for drawer, 'base' for full-page */
  size?: 'sm' | 'base';
}

/**
 * Renders agent/user message content as Markdown.
 * Handles streaming gracefully — react-markdown just re-parses on each update.
 */
const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  size = 'sm',
}) => {
  const textSize = size === 'base' ? 'text-base leading-relaxed' : 'text-sm';

  // Secondary elements (table borders, hr, blockquote, code) — token-driven so
  // the message reads correctly on the light user/assistant surfaces in either
  // theme. isUser no longer changes colors (user bubbles are neutral, not blue).
  const mutedBorder = 'border-border';
  const mutedText = 'text-muted-foreground';
  const codeBg = 'bg-muted';
  const blockCodeBg = 'bg-muted';

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Paragraphs
        p: ({ children }) => (
          <p className={`${textSize} mb-2 last:mb-0`}>{children}</p>
        ),
        // Bold / italic
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        // Headings
        h1: ({ children }) => <p className={`${textSize} font-bold mb-2`}>{children}</p>,
        h2: ({ children }) => <p className={`${textSize} font-bold mb-2`}>{children}</p>,
        h3: ({ children }) => <p className={`${textSize} font-semibold mb-1`}>{children}</p>,
        // Lists
        ul: ({ children }) => <ul className={`${textSize} list-disc pl-4 mb-2 last:mb-0 space-y-0.5`}>{children}</ul>,
        ol: ({ children }) => <ol className={`${textSize} list-decimal pl-4 mb-2 last:mb-0 space-y-0.5`}>{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        // Inline code
        code: ({ children, className }) => {
          // Block code has a className like "language-xxx" from the parent pre
          if (className) {
            return <code className={`${textSize} block`}>{children}</code>;
          }
          return (
            <code className={`${codeBg} px-1 py-0.5 rounded text-[0.85em]`}>
              {children}
            </code>
          );
        },
        // Code blocks
        pre: ({ children }) => (
          <pre className={`${blockCodeBg} rounded p-2 mb-2 last:mb-0 overflow-x-auto text-xs`}>
            {children}
          </pre>
        ),
        // Tables
        table: ({ children }) => (
          <div className="overflow-x-auto mb-2 last:mb-0">
            <table className={`${textSize} border-collapse w-full`}>{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className={`border-b ${mutedBorder}`}>{children}</thead>,
        th: ({ children }) => (
          <th className={`text-left px-2 py-1 font-semibold ${textSize}`}>{children}</th>
        ),
        td: ({ children }) => (
          <td className={`px-2 py-1 border-t ${mutedBorder}`}>{children}</td>
        ),
        // Blockquote
        blockquote: ({ children }) => (
          <blockquote className={`border-l-2 ${mutedBorder} ${mutedText} pl-3 mb-2 last:mb-0 italic`}>
            {children}
          </blockquote>
        ),
        // Horizontal rule
        hr: () => <hr className={`${mutedBorder} my-2`} />,
        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            {children}
          </a>
        ),
      }}
    >
      {content}
    </Markdown>
  );
};

export default MarkdownMessage;
